/**
 * Saved filters — over contacts, and over the inbox.
 *
 * One engine for both, because a segment and a saved view are the same thing pointed at
 * different rows: a set of conditions, evaluated live rather than stored as a list of ids. A
 * frozen list is wrong within the hour, and a customer who segments on "has not messaged in
 * thirty days" means thirty days from today, not from the afternoon they wrote it.
 *
 * The subtlety here is what a condition means when the field is simply absent. A contact with
 * no recorded city is not the same as a contact whose city is not Kolkata, and every database
 * that has ever conflated the two has sent somebody the wrong campaign.
 */

export type FieldType = 'text' | 'number' | 'date' | 'boolean' | 'set'

export type Operator =
  | 'is'
  | 'is_not'
  | 'contains'
  | 'not_contains'
  | 'is_set'
  | 'is_not_set'
  | 'before'
  | 'after'
  | 'within_days'
  | 'not_within_days'
  | 'greater_than'
  | 'less_than'

export const OPERATOR_LABELS: Record<Operator, string> = {
  is: 'is',
  is_not: 'is not',
  contains: 'contains',
  not_contains: 'does not contain',
  is_set: 'has any value',
  is_not_set: 'is empty',
  before: 'is before',
  after: 'is after',
  within_days: 'in the last',
  not_within_days: 'not in the last',
  greater_than: 'is more than',
  less_than: 'is fewer than',
}

/** Which operators make sense for which kind of field. */
export const OPERATORS_FOR: Record<FieldType, Operator[]> = {
  text: ['is', 'is_not', 'contains', 'not_contains', 'is_set', 'is_not_set'],
  number: ['is', 'greater_than', 'less_than', 'is_set', 'is_not_set'],
  date: ['within_days', 'not_within_days', 'before', 'after', 'is_set', 'is_not_set'],
  boolean: ['is'],
  set: ['contains', 'not_contains', 'is_set', 'is_not_set'],
}

export interface FieldDefinition {
  key: string
  label: string
  type: FieldType
  /** Fixed choices, where the field has them. */
  options?: string[]
  hint?: string
}

export interface Condition {
  field: string
  operator: Operator
  value?: string
}

/**
 * One level of nesting: "(A or B) and (C or D)" — a group of conditions with its own match
 * mode, sitting alongside plain conditions in a segment's top-level list. Distinguished from a
 * `Condition` at runtime by carrying a `conditions` array, which a leaf condition never has.
 *
 * Deliberately capped at one level. A segment/saved-view/automation-rule's stored
 * `conditions` array was always a flat `Condition[]` before this existed, so treating "no
 * groups present" as the common case and "one level of grouping" as the ceiling keeps every
 * already-stored filter valid without a migration, while still covering the condition shapes
 * that actually come up ("category is Refunds or Delivery, and priority is urgent or high").
 */
export interface ConditionGroup {
  match: 'all' | 'any'
  conditions: Condition[]
}

export type ConditionEntry = Condition | ConditionGroup

export function isConditionGroup(entry: ConditionEntry): entry is ConditionGroup {
  return Array.isArray((entry as ConditionGroup).conditions)
}

export interface Segment {
  /** Every entry must hold, or any one of them. An entry is a condition or a group of them. */
  match: 'all' | 'any'
  conditions: ConditionEntry[]
}

/* ---------------------------------------------------------------- fields */

export const CONTACT_FIELDS: FieldDefinition[] = [
  { key: 'name', label: 'Name', type: 'text' },
  { key: 'phone', label: 'Phone', type: 'text' },
  { key: 'company', label: 'Company', type: 'text' },
  { key: 'tags', label: 'Tags', type: 'set' },
  { key: 'lastMessageAt', label: 'Last messaged', type: 'date' },
  { key: 'createdAt', label: 'First seen', type: 'date' },
  { key: 'messageCount', label: 'Messages exchanged', type: 'number' },
  { key: 'openTickets', label: 'Open tickets', type: 'number' },
  { key: 'labels', label: 'Labels on their chat', type: 'set' },
  { key: 'optedOut', label: 'Asked us to stop', type: 'boolean', options: ['yes', 'no'] },
]

export const CONVERSATION_FIELDS: FieldDefinition[] = [
  { key: 'kind', label: 'Chat type', type: 'text', options: ['direct', 'group'] },
  { key: 'labels', label: 'Labels', type: 'set' },
  { key: 'status', label: 'Ticket status', type: 'text', options: ['open', 'pending', 'resolved', 'closed', 'none'] },
  { key: 'priority', label: 'Ticket priority', type: 'text', options: ['low', 'normal', 'high', 'urgent'] },
  { key: 'category', label: 'Ticket category', type: 'text' },
  { key: 'assignee', label: 'Assigned to', type: 'text' },
  { key: 'unread', label: 'Unread messages', type: 'number' },
  { key: 'breached', label: 'Past its reply target', type: 'boolean', options: ['yes', 'no'] },
  { key: 'awaitingReply', label: 'Waiting on us', type: 'boolean', options: ['yes', 'no'] },
  { key: 'lastMessageAt', label: 'Last message', type: 'date' },
  { key: 'channelNumber', label: 'Received on', type: 'text' },
]

/* ---------------------------------------------------------------- matching */

/** What a row looks like to the matcher: a flat bag of values it can reason about. */
export type Subject = Record<string, string | number | boolean | Date | string[] | null | undefined>

/**
 * Whether a value counts as absent.
 *
 * An empty string and an empty list are absent, not "a value that happens to be empty". A
 * contact whose name was never captured and one whose name is a space are the same contact as
 * far as anyone segmenting is concerned.
 */
function isAbsent(value: Subject[string]): boolean {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  return false
}

const text = (value: Subject[string]): string =>
  value instanceof Date ? value.toISOString() : String(value ?? '').trim().toLowerCase()

function asDate(value: Subject[string]): Date | null {
  if (value instanceof Date) return value
  if (typeof value === 'string' && value) {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  return null
}

function asNumber(value: Subject[string]): number | null {
  if (typeof value === 'number') return value
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    return Number.isNaN(parsed) ? null : parsed
  }
  return null
}

/**
 * Evaluates one condition.
 *
 * The absent case is decided first and deliberately. A negative condition — "tag is not vip",
 * "city does not contain London" — holds for a row that has no value at all, because it is
 * true that such a row is not vip. Excluding them instead is the mistake SQL's NULL semantics
 * trains people into, and in a campaign it silently drops every contact whose record is
 * merely incomplete.
 */
export function matchesCondition(condition: Condition, subject: Subject, now: Date): boolean {
  const value = subject[condition.field]
  const absent = isAbsent(value)
  const wanted = (condition.value ?? '').trim()

  switch (condition.operator) {
    case 'is_set':
      return !absent
    case 'is_not_set':
      return absent

    case 'is':
      if (absent) return false
      if (typeof value === 'boolean') return (value ? 'yes' : 'no') === wanted.toLowerCase()
      if (Array.isArray(value)) return value.some((entry) => text(entry) === wanted.toLowerCase())
      return text(value) === wanted.toLowerCase()

    case 'is_not':
      // True for an absent value: a contact with no city is not in London.
      if (absent) return true
      if (Array.isArray(value)) return !value.some((entry) => text(entry) === wanted.toLowerCase())
      return text(value) !== wanted.toLowerCase()

    case 'contains':
      if (absent) return false
      if (Array.isArray(value)) return value.some((entry) => text(entry).includes(wanted.toLowerCase()))
      return text(value).includes(wanted.toLowerCase())

    case 'not_contains':
      if (absent) return true
      if (Array.isArray(value)) return !value.some((entry) => text(entry).includes(wanted.toLowerCase()))
      return !text(value).includes(wanted.toLowerCase())

    case 'before': {
      const date = asDate(value)
      const limit = asDate(wanted)
      return date !== null && limit !== null && date < limit
    }
    case 'after': {
      const date = asDate(value)
      const limit = asDate(wanted)
      return date !== null && limit !== null && date > limit
    }

    case 'within_days': {
      const date = asDate(value)
      const days = Number(wanted)
      if (date === null || !Number.isFinite(days)) return false
      return now.getTime() - date.getTime() <= days * 86_400_000
    }
    case 'not_within_days': {
      const date = asDate(value)
      const days = Number(wanted)
      if (!Number.isFinite(days)) return false
      // Someone who has never messaged has not messaged in the last thirty days, and is very
      // often exactly who a win-back campaign is looking for.
      if (date === null) return true
      return now.getTime() - date.getTime() > days * 86_400_000
    }

    case 'greater_than': {
      const number = asNumber(value)
      const limit = asNumber(wanted)
      return number !== null && limit !== null && number > limit
    }
    case 'less_than': {
      const number = asNumber(value)
      const limit = asNumber(wanted)
      // A missing count is zero, not unknown: nobody has "an unknown number of open tickets".
      return (number ?? 0) < (limit ?? 0)
    }

    default:
      return false
  }
}

/** Whether an entry has anything live in it — a leaf with a field chosen, or a group with at
 *  least one such leaf. An empty group is exactly as inert as a leaf with no field picked.
 *  Exported so UI code can gate "is there anything to preview/save yet" the same way the
 *  matcher decides "is there anything to evaluate", instead of re-deriving the rule twice. */
export function isLiveEntry(entry: ConditionEntry): boolean {
  return isConditionGroup(entry) ? entry.conditions.some((c) => c.field) : Boolean(entry.field)
}

function matchesEntry(entry: ConditionEntry, subject: Subject, now: Date): boolean {
  if (!isConditionGroup(entry)) return matchesCondition(entry, subject, now)
  const live = entry.conditions.filter((c) => c.field)
  if (live.length === 0) return false
  return entry.match === 'any'
    ? live.some((c) => matchesCondition(c, subject, now))
    : live.every((c) => matchesCondition(c, subject, now))
}

/**
 * Whether a row belongs in the segment.
 *
 * A segment with no conditions matches nothing rather than everything. Matching everything is
 * what an empty filter means in most query languages and it is the wrong default here: the
 * commonest way to use this is to pick a segment as the audience for a bulk send, and an
 * accidental "everyone" is a campaign to the entire contact list.
 *
 * A top-level entry can be a plain condition or a group of them (one level deep — see
 * `ConditionGroup`); a group evaluates its own conditions by its own match mode before being
 * combined into the outer one, which is what makes "(A or B) and (C or D)" expressible.
 */
export function matchesSegment(segment: Segment, subject: Subject, now = new Date()): boolean {
  const live = segment.conditions.filter(isLiveEntry)
  if (live.length === 0) return false

  return segment.match === 'any'
    ? live.some((entry) => matchesEntry(entry, subject, now))
    : live.every((entry) => matchesEntry(entry, subject, now))
}

export function filterBySegment<T extends Subject>(
  rows: T[],
  segment: Segment,
  now = new Date(),
): T[] {
  const live = segment.conditions.filter(isLiveEntry)
  if (live.length === 0) return []
  return rows.filter((row) => matchesSegment(segment, row, now))
}

/* ---------------------------------------------------------------- describing */

function validateCondition(condition: Condition, fields: FieldDefinition[], problems: string[]): void {
  if (!condition.field) return
  const known = new Map(fields.map((field) => [field.key, field]))
  const field = known.get(condition.field)
  if (!field) {
    problems.push(`There is no field called “${condition.field}”.`)
    return
  }

  if (!OPERATORS_FOR[field.type].includes(condition.operator)) {
    problems.push(`${field.label} cannot be filtered by “${OPERATOR_LABELS[condition.operator]}”.`)
    return
  }

  // Only these two stand alone; everything else compares against something.
  const needsValue = condition.operator !== 'is_set' && condition.operator !== 'is_not_set'
  if (needsValue && !(condition.value ?? '').trim()) {
    problems.push(`${field.label} “${OPERATOR_LABELS[condition.operator]}” needs a value.`)
    return
  }

  if (
    (condition.operator === 'within_days' || condition.operator === 'not_within_days') &&
    !Number.isFinite(Number(condition.value))
  ) {
    problems.push(`${field.label} needs a number of days.`)
  }
}

export function validateSegment(segment: Segment, fields: FieldDefinition[]): string[] {
  const problems: string[] = []

  if (segment.conditions.filter(isLiveEntry).length === 0) {
    problems.push('Add at least one condition, or this will match nobody.')
  }

  for (const entry of segment.conditions) {
    if (isConditionGroup(entry)) {
      for (const condition of entry.conditions) validateCondition(condition, fields, problems)
      continue
    }
    validateCondition(entry, fields, problems)
  }

  return problems
}

function describeCondition(condition: Condition, known: Map<string, FieldDefinition>): string {
  const label = known.get(condition.field)?.label ?? condition.field
  const operator = OPERATOR_LABELS[condition.operator]

  if (condition.operator === 'is_set' || condition.operator === 'is_not_set') {
    return `${label} ${operator}`
  }
  if (condition.operator === 'within_days' || condition.operator === 'not_within_days') {
    const days = Number(condition.value)
    return `${label} ${operator} ${days} ${days === 1 ? 'day' : 'days'}`
  }
  return `${label} ${operator} “${condition.value}”`
}

/** A sentence a person can check, which is the only way anyone spots a wrong filter. */
export function describeSegment(segment: Segment, fields: FieldDefinition[]): string {
  const known = new Map(fields.map((field) => [field.key, field]))
  const parts = segment.conditions
    .filter(isLiveEntry)
    .map((entry) => {
      if (!isConditionGroup(entry)) return describeCondition(entry, known)
      const inner = entry.conditions
        .filter((c) => c.field)
        .map((c) => describeCondition(c, known))
        .join(entry.match === 'any' ? ' or ' : ' and ')
      return entry.conditions.filter((c) => c.field).length > 1 ? `(${inner})` : inner
    })

  if (parts.length === 0) return 'Matches nobody until a condition is added'
  return parts.join(segment.match === 'any' ? ' or ' : ' and ')
}

/* ---------------------------------------------------------------- presets */

/** Segments worth offering, because they are the ones people build by hand anyway. */
export const CONTACT_PRESETS: { name: string; segment: Segment }[] = [
  {
    name: 'Gone quiet',
    segment: {
      match: 'all',
      conditions: [
        { field: 'lastMessageAt', operator: 'not_within_days', value: '30' },
        { field: 'optedOut', operator: 'is', value: 'no' },
      ],
    },
  },
  {
    name: 'Active this week',
    segment: {
      match: 'all',
      conditions: [{ field: 'lastMessageAt', operator: 'within_days', value: '7' }],
    },
  },
  {
    name: 'Has something open',
    segment: {
      match: 'all',
      conditions: [{ field: 'openTickets', operator: 'greater_than', value: '0' }],
    },
  },
]

export const VIEW_PRESETS: { name: string; segment: Segment }[] = [
  {
    name: 'Past the reply target',
    segment: { match: 'all', conditions: [{ field: 'breached', operator: 'is', value: 'yes' }] },
  },
  {
    name: 'Waiting on us',
    segment: { match: 'all', conditions: [{ field: 'awaitingReply', operator: 'is', value: 'yes' }] },
  },
  {
    name: 'Urgent and open',
    segment: {
      match: 'all',
      conditions: [
        { field: 'priority', operator: 'is', value: 'urgent' },
        { field: 'status', operator: 'is', value: 'open' },
      ],
    },
  },
  {
    name: 'Nobody assigned',
    segment: {
      match: 'all',
      conditions: [
        { field: 'assignee', operator: 'is_not_set' },
        { field: 'status', operator: 'is', value: 'open' },
      ],
    },
  },
]
